import { useState, useEffect } from 'react'

import {
  getBrowserType,
  supportsInstallPrompt,
  isPWAInstalled,
  getInstallInstructions,
  markPWAAsInstalled,
  wasInstallPromptDismissed,
  markInstallPromptDismissed
} from './helpers'


interface T_BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

interface T_PWAInstallHook {
  canInstall: boolean
  isInstalled: boolean
  isInstallAvailable: boolean // true если можно установить (без учёта isDismissed)
  browserType: 'chrome' | 'safari' | 'yandex' | 'firefox' | 'edge' | 'other'
  supportsPromptAPI: boolean
  installInstructions: string
  promptInstall: () => Promise<void>
  dismissPrompt: () => void
  isDismissed: boolean
}

/**
 * Хук для работы с PWA установкой
 */
export const usePWAInstall = (): T_PWAInstallHook => {
  const [ deferredPrompt, setDeferredPrompt ] = useState<T_BeforeInstallPromptEvent | null>(null)
  const [ canInstall, setCanInstall ] = useState(false)
  const [ isInstalled, setIsInstalled ] = useState(false)
  const [ isDismissed, setIsDismissed ] = useState(false)

  const browserType = getBrowserType()
  const supportsPromptAPI = supportsInstallPrompt()
  const installInstructions = getInstallInstructions(browserType)

  useEffect(() => {
    // Проверяем, установлено ли PWA
    setIsInstalled(isPWAInstalled())

    // Проверяем, был ли промпт отклонен
    setIsDismissed(wasInstallPromptDismissed())

    // Слушаем событие beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const promptEvent = e as T_BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)
      setCanInstall(true)
    }

    // Слушаем событие appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setCanInstall(false)
      setDeferredPrompt(null)
      markPWAAsInstalled()
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    // Для браузеров, которые не поддерживают beforeinstallprompt
    if (!supportsPromptAPI && !isPWAInstalled()) {
      setCanInstall(true)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const promptInstall = async (): Promise<void> => {
    if (deferredPrompt) {
      // Используем нативный промпт для поддерживаемых браузеров
      try {
        await deferredPrompt.prompt()
        const choiceResult = await deferredPrompt.userChoice

        if (choiceResult.outcome === 'accepted') {
          setIsInstalled(true)
          markPWAAsInstalled()
        } else {
          setIsDismissed(true)
          markInstallPromptDismissed()
        }

        setDeferredPrompt(null)
        setCanInstall(false)
      } catch (error) {
        console.error('Ошибка при показе промпта установки:', error)
      }
    }
  }

  const dismissPrompt = (): void => {
    setIsDismissed(true)
    setCanInstall(false)
    markInstallPromptDismissed()
  }

  return {
    canInstall: canInstall && !isInstalled && !isDismissed,
    isInstalled,
    isInstallAvailable: canInstall && !isInstalled, // без учёта isDismissed
    browserType,
    supportsPromptAPI,
    installInstructions,
    promptInstall,
    dismissPrompt,
    isDismissed
  }
}
